import React from 'react'
import styled, { css } from 'styled-components/macro'
import useField from 'components/Form/useField'
import icon from 'styles/mixins/icon'
import Icon from 'components/misc/Icon'
import { body2, caption } from 'styles/mixins/textstyles'

const CheckRoot = styled.div`
  padding: 0;
  position: relative;

  .check-field {
    input {
      width: 2rem;
      height: 2rem;
      position: absolute;
      top: 1.5rem;
      left: 0;
      z-index: 1;
      opacity: 0;
      appearance: none;
      cursor: pointer;
    }
  
    label {
      ${body2('black')}
      
      margin-left: 2.8rem;
      padding-top: ${props => props.tight ? '.6rem' : '2.2rem'};
    }
  }
`

const CheckSquare = styled.span`
  ${icon()}
  
  font-size: 1.2rem;
  background: ${props => props.theme.colors.greyLighter};
  width: 2rem;
  height: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  border-radius: 2px;
  position: absolute;
  top: ${props => props.tight ? '.5rem' : '2.1rem'};
  left: 0;
  z-index: 0;

  ${props => props.checked && css`
    background: ${props => props.theme.colors.primaryBase};
  `}
`

const CheckIcon = styled(Icon).attrs({ symbol: 'check' })`
  visibility: ${props => props.checked ? 'visible' : 'hidden'};
  fill: white;
  width: 1.6rem;
`

const Check = ({ checked, tight }) => (
  <CheckSquare checked={checked} tight={tight}><CheckIcon checked={checked} /></CheckSquare>
)

const CheckChildren = styled.div`
  margin-left: 2.8rem;
  margin-top: .8rem;
`

const LabelText = styled.label`
  ${caption('black', 'medium')}
  display: block;
  text-align: left;
  color: ${props => props.theme.colors.greyBase};
  font-size: 1.3rem;
  padding-top: ${props => props.tight ? '.2rem' : '1rem'};
  padding-bottom: .1rem;

  ${props => props.focused && css`
    color: ${props => props.theme.colors.primaryBase};
  `}

  ${props => props.error && css`
    color: ${props => props.theme.colors.errorBase};
  `}
`

const Label = props => {
  const { label, error, focused, tight, id, htmlFor } = props
  const text = error || label || '\u00A0'
  return <LabelText focused={focused} error={error} tight={tight} id={id} htmlFor={htmlFor}>{text}</LabelText>
}

const Checkbox = props => {
  const { defaultChecked:defaultValue, name, validate, label, tight, ...rest } = props
  const { value, setValue, setChanged, error, id } = useField({ name, validate, defaultValue })


  const handleChange = event => {
    setValue(!!event.target.checked)
    setChanged()
    if (props.onChange) {
      event.persist()
      props.onChange(event)
    }
  }

  const renderChildren = () => {
    const children = props.children && props.children(value)
    if (children) {
      return(
        <CheckChildren>
          {children}
        </CheckChildren>
      )
    }
  }

  delete rest.children
  delete rest.defaultChecked

  return (
    <CheckRoot tight={tight}>
      <div className="check-field">
        <Label
          id={`${rest.id || id}-label`}
          htmlFor={rest.id || id}
          label={label}
          error={error}
          tight={props.tight}
        />
        <input id={id} type="checkbox" name={name} {...rest} onChange={handleChange} checked={value} />
      </div>
      <Check checked={value} tight={tight} />
      {renderChildren()}
    </CheckRoot>
  )
}

export default Checkbox